// KnowledgeBank.RelationId — the stable id of a first-class Relation
// entity.
//
// Per `docs/model/bcs/knowledge-bank.md` §2 + IDD `bd4eafbc`: a relation
// is an id'd entity, not a branded name string, so the triple's middle
// leg is a `RelationId`. The id is the **stable handle** — renaming a
// relation's `name` (via `RenameRelation`) keeps its id, so every citing
// triple is untouched; a branded name string would have forced a rewrite
// of every triple.
//
// **Id scheme (MVP): `rel:<name>`.** Every relation's id is derived from
// its name at creation via `forName`. This makes three things total pure
// functions: name → id resolution at the app/tool boundary, the seeded
// catalogue, and the legacy-log migration (`assertion_added` v1→v2 maps
// `relation` name → `relationId`). It is still true reification — the id
// is fixed at creation and outlives any later rename of the `name`; the
// `rel:` prefix just makes the initial spelling readable. The one MVP
// limitation: two relations created with the same name would collide on
// id (names are unique vocabulary, so this never bites). A post-MVP mint
// (opaque uuid at `DefineRelation`) can replace `forName` for
// author-defined relations without reshaping the type.
export { annotates, answersTo, broader, citesElement, contradicts, covers, definedIn, derivesFrom, drawsOn, explains, forName, hasActor, hasDescription, hasQualityScore, hasTag, mk, oaEnd, oaExact, oaHasSelector, oaHasSource, oaPrefix, oaStart, oaSuffix, practises, prerequisiteOf, provGeneratedAtTime, provWasAttributedTo, provWasDerivedFrom, rdfValue, related, schemaAbout, schemaAudience, schemaText, serves, supports, teaches, touches, };
import * as RelationName from "./relationName.js";
// The brand smart constructor — the one sanctioned `as` per the brand
// rule. Trusts its input; callers derive ids via `forName`.
const mk = (s) => s;
// Deterministic id for a relation NAME. Total + pure — the substrate for
// name→id resolution and the legacy-log migration. Accepts a raw string
// so the migration (which reads an untyped wire name) can call it too.
const forName = (name) => mk(`rel:${name}`);
// === Seeded id consts (parallel to the RelationName consts) ===
//
// Call sites that name a relation by its seeded const (core `react.ts`,
// the KB workflows, the agent tools) reach for these instead of
// re-deriving `forName(RelationName.x)` at every use.
const teaches = forName(RelationName.teaches);
const covers = forName(RelationName.covers);
const touches = forName(RelationName.touches);
const prerequisiteOf = forName(RelationName.prerequisiteOf);
const derivesFrom = forName(RelationName.derivesFrom);
// `serves` — whole-material → the LearningContext it is authored for
// (twin of `derives-from`; the goal-anchor edge the CEGIS score reads).
const serves = forName(RelationName.serves);
const hasDescription = forName(RelationName.hasDescription);
const hasTag = forName(RelationName.hasTag);
const hasQualityScore = forName(RelationName.hasQualityScore);
const contradicts = forName(RelationName.contradicts);
const supports = forName(RelationName.supports);
const annotates = forName(RelationName.annotates);
const hasActor = forName(RelationName.hasActor);
const citesElement = forName(RelationName.citesElement);
// Goal coverage (see `relationName.ts`).
const answersTo = forName(RelationName.answersTo);
const definedIn = forName(RelationName.definedIn);
const explains = forName(RelationName.explains);
const practises = forName(RelationName.practises);
// The goal as a projection (see `relationName.ts`).
const broader = forName(RelationName.broader);
const drawsOn = forName(RelationName.drawsOn);
// Concepts that belong together (D55 S10).
const related = forName(RelationName.related);
// Claims (see `relationName.ts`).
const schemaText = forName(RelationName.schemaText);
const schemaAbout = forName(RelationName.schemaAbout);
const schemaAudience = forName(RelationName.schemaAudience);
const provWasAttributedTo = forName(RelationName.provWasAttributedTo);
const provGeneratedAtTime = forName(RelationName.provGeneratedAtTime);
const provWasDerivedFrom = forName(RelationName.provWasDerivedFrom);
const oaHasSource = forName(RelationName.oaHasSource);
const oaHasSelector = forName(RelationName.oaHasSelector);
const oaExact = forName(RelationName.oaExact);
const oaPrefix = forName(RelationName.oaPrefix);
const oaSuffix = forName(RelationName.oaSuffix);
const oaStart = forName(RelationName.oaStart);
const oaEnd = forName(RelationName.oaEnd);
const rdfValue = forName(RelationName.rdfValue);
//# sourceMappingURL=relationId.js.map